import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_CURRENT_SCHOOL_USER } from '../Config/ApiUrlConstant';

const AuthContext = createContext();
const SCHOOL_TOKEN_KEY = 'school_token';
const SCHOOL_ROLE_KEY = 'school_role';

export const AuthProvider = ({ children }) => {
  const storedToken = localStorage.getItem(SCHOOL_TOKEN_KEY);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(storedToken));
  const [authReady, setAuthReady] = useState(!storedToken);
  const [role, setRole] = useState(localStorage.getItem(SCHOOL_ROLE_KEY) || '');
  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [teacherAccess, setTeacherAccess] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const clearSession = (redirect = true) => {
    localStorage.removeItem(SCHOOL_TOKEN_KEY);
    localStorage.removeItem(SCHOOL_ROLE_KEY);
    setRole(''); setCurrentUser(null); setPermissions([]); setTeacherAccess(null); setMustChangePassword(false);
    setIsAuthenticated(false); setAuthReady(true);
    if (redirect) window.location.href = '/login';
  };

  const login = (token, userRole, passwordChangeRequired = false) => {
    localStorage.setItem(SCHOOL_TOKEN_KEY, token);
    localStorage.setItem(SCHOOL_ROLE_KEY, userRole || '');
    setRole(userRole || ''); setPermissions([]); setTeacherAccess(null);
    setMustChangePassword(Boolean(passwordChangeRequired)); setAuthReady(false); setIsAuthenticated(true);
  };
  const logout = () => clearSession(true);
  const can = (code) => permissions.includes(code);

  useEffect(() => {
    const originalFetch = window.fetch;
    const interceptorId = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem(SCHOOL_TOKEN_KEY);
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    window.fetch = (url, options = {}) => {
      const token = localStorage.getItem(SCHOOL_TOKEN_KEY);
      return originalFetch(url, token ? { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } } : options);
    };
    return () => { window.fetch = originalFetch; axios.interceptors.request.eject(interceptorId); };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    fetch(API_CURRENT_SCHOOL_USER, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(async (response) => ({ response, payload: await response.json().catch(() => ({})) }))
      .then(({ response, payload }) => {
        if (!active) return;
        if (!response.ok || !payload.status) { clearSession(false); return; }
        const data = payload.data || {}; const currentRole = data.role || '';
        localStorage.setItem(SCHOOL_ROLE_KEY, currentRole);
        setRole(currentRole); setCurrentUser(data); setPermissions(data.permissions || []);
        setTeacherAccess(data.teacher_access || null); setMustChangePassword(Boolean(data.must_change_password)); setAuthReady(true);
      })
      .catch(() => { if (active) clearSession(false); });
    return () => { active = false; };
  }, [isAuthenticated]);

  const value = { isAuthenticated, authReady, role, currentUser, permissions, teacherAccess, mustChangePassword, can, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
